import MerideVideo from './MerideVideo';

const MEDIA_EXPLORER_MAIN_CLASS = 'mfp-media-explorer mfp-with-zoom';
const THUMBS_CLASS = 'mfp-media-explorer-thumbs';
const CAPTION_CLASS = 'mfp-media-explorer-caption';
const FRAME_CLASS = 'mfp-media-explorer-frame';
const STAGE_CLASS = 'mfp-media-explorer-stage';
const SLIDE_SELECTOR = '.media-explorer-slide';
const GALLERY_SELECTOR = '.media-explorer-gallery';
const ACTIVE_SLIDE_KEY = 'mediaExplorerActiveSlide';
const THUMBS_ROW_CLASS = 'mfp-media-explorer-thumbs-row';
const THUMBS_NAV_CLASS = 'mfp-media-explorer-thumbs-nav';
const THUMB_PAGE_KEY = 'mediaExplorerThumbPage';
const THUMB_SLOT_PX = 72;
const THUMBS_NAV_RESERVE_PX = 80;
const THUMBS_PAGE_MAX = 10;

let is_document_bound = false;

/** @type {HTMLElement[]} */
let open_galleries = [];

/**
 * One Magnific item per content card. Arrows change card; thumbs change media in-place.
 *
 * Each Magnific item uses an empty mount node. We always paint from
 * open_galleries[instance.index] so items never share/reuse rendered media DOM.
 */
function bindMediaExplorerDocument() {
	if (is_document_bound) {
		return;
	}

	is_document_bound = true;

	$(document).on('click.mediaExplorer', `${GALLERY_SELECTOR} ${SLIDE_SELECTOR}`, function (event) {
		event.preventDefault();
		event.stopPropagation();

		const $clicked_slide = $(this);
		const $gallery = $clicked_slide.closest(GALLERY_SELECTOR);
		if (!$gallery.length) {
			return;
		}

		// Scope to the list page, NOT the individual card (.card also has class "panel").
		const $root = $gallery.closest('.page-content, .offcanvas-body, body');
		const $galleries = $root.find(GALLERY_SELECTOR);
		if ($galleries.length === 0) {
			return;
		}

		const gallery_index = $galleries.index($gallery);
		if (gallery_index < 0) {
			return;
		}

		const slide_index = Math.max(0, $gallery.find(SLIDE_SELECTOR).index($clicked_slide));
		$gallery.data(ACTIVE_SLIDE_KEY, slide_index);

		open_galleries = $galleries.toArray();

		const items = open_galleries.map(() => ({
			type: 'inline',
			src: $('<div class="mfp-media-explorer-mount"></div>'),
		}));

		// Magnific's static open(options, index) overwrites options.index with the 2nd arg
		// (`index || 0`). Passing the card index only inside the object always opens item 0.
		$.magnificPopup.open({
			items,
			type: 'inline',
			closeOnContentClick: false,
			closeOnBgClick: true,
			// Close on wrap (fixed), not inside the centered frame — avoids z-index/under-overlay issues.
			closeBtnInside: false,
			showCloseBtn: true,
			tClose: 'Chiudi (Esc)',
			mainClass: MEDIA_EXPLORER_MAIN_CLASS,
			fixedContentPos: true,
			overflowY: 'hidden',
			removalDelay: 0,
			preloader: false,
			gallery: {
				enabled: items.length > 1,
			},
			callbacks: {
				open() {
					paintCurrent(this);
				},
				change() {
					cleanupMediaExplorerMedia();
					paintCurrent(this);
				},
				close() {
					cleanupMediaExplorerMedia();
					open_galleries = [];
				},
			},
		}, gallery_index);
	});
}

/**
 * @param {object} instance
 */
function paintCurrent(instance) {
	const gallery_el = open_galleries[instance.index ?? 0];
	if (!gallery_el) {
		return;
	}

	const $gallery = $(gallery_el);
	const $slides = $gallery.find(SLIDE_SELECTOR);
	if (!$slides.length) {
		return;
	}

	let active_index = Number($gallery.data(ACTIVE_SLIDE_KEY) ?? 0);
	if (active_index < 0 || active_index >= $slides.length) {
		active_index = 0;
		$gallery.data(ACTIVE_SLIDE_KEY, 0);
	}

	const $mount = resolveMount(instance);
	if (!$mount?.length) {
		return;
	}

	const $frame = buildFrameFromSlide($slides.eq(active_index));
	$mount.empty().append($frame);
	fillThumbStrip(instance, $gallery, active_index, { follow_active: true });
}

/**
 * @param {object} instance
 * @returns {JQuery|null}
 */
function resolveMount(instance) {
	if (instance.content?.length) {
		return instance.content;
	}

	return instance.container?.find('.mfp-content').first() ?? null;
}

/**
 * @param {JQuery} $row
 * @returns {number}
 */
function measureThumbsPageSize($row) {
	const width = $row[0]?.clientWidth ?? 0;

	if (width < THUMBS_NAV_RESERVE_PX) {
		return THUMBS_PAGE_MAX;
	}

	return Math.min(THUMBS_PAGE_MAX, Math.max(4, Math.floor((width - THUMBS_NAV_RESERVE_PX) / THUMB_SLOT_PX)));
}

/**
 * @param {object} instance
 * @param {JQuery} $gallery
 * @param {number} active_index
 * @param {{ follow_active?: boolean, page?: number }} [options]
 */
function fillThumbStrip(instance, $gallery, active_index, options = {}) {
	const $mount = resolveMount(instance);
	const $thumbs = $mount?.find(`.${THUMBS_CLASS}`);
	if (!$thumbs?.length) {
		return;
	}

	const $row = $thumbs.closest(`.${THUMBS_ROW_CLASS}`);
	const $slides = $gallery.find(SLIDE_SELECTOR);

	if ($slides.length <= 1) {
		$row.hide();
		$thumbs.empty();

		return;
	}

	$row.show();

	const page_size = measureThumbsPageSize($row);
	const page_count = Math.max(1, Math.ceil($slides.length / page_size));
	let page = Number.isInteger(options.page)
		? options.page
		: options.follow_active
			? Math.floor(active_index / page_size)
			: Number($gallery.data(THUMB_PAGE_KEY) ?? 0);

	if (!Number.isFinite(page) || page < 0) {
		page = 0;
	}

	page = Math.min(page_count - 1, page);
	$gallery.data(THUMB_PAGE_KEY, page);

	const start = page * page_size;
	const end = Math.min($slides.length, start + page_size);

	$thumbs.empty();

	for (let index = start; index < end; index += 1) {
		const $el = $slides.eq(index);
		const media_type = $el.attr('data-media-type') ?? 'image';
		const thumb_url = $el.attr('data-thumb') ?? '';
		const caption = $el.attr('title') ?? '';

		const $button = $('<button>', {
			type: 'button',
			class: 'mfp-media-explorer-thumb',
			'data-index': index,
			'data-media-type': media_type,
			title: caption,
			'aria-label': caption || `Media ${index + 1}`,
		});

		if (media_type === 'image') {
			const usage = resolveSourceUsageFromSlide($el);
			const media_url = $el.attr('href') ?? '';
			$button.attr({
				draggable: 'true',
				'data-media-url': media_url,
			});
			if (usage.source && usage.identifier) {
				$button.attr({
					'data-source': usage.source,
					'data-source-id': usage.identifier,
				});
			}
		}

		if (index === active_index) {
			$button.addClass('is-active');
		}

		if (thumb_url) {
			$('<img>', { src: thumb_url, alt: '', loading: 'lazy' }).appendTo($button);
		} else {
			$('<i>', {
				class: mediaIconClass(media_type, $el.attr('data-video-type')),
				'aria-hidden': 'true',
			}).appendTo($button);
		}

		$button.on('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			$gallery.data(ACTIVE_SLIDE_KEY, index);
			cleanupMediaExplorerMedia();
			paintCurrent(instance);
		});

		$thumbs.append($button);
	}

	const show_nav = page_count > 1;
	const $prev = $row.find(`.${THUMBS_NAV_CLASS}.is-prev`);
	const $next = $row.find(`.${THUMBS_NAV_CLASS}.is-next`);

	$prev.toggle(show_nav).prop('disabled', page <= 0);
	$next.toggle(show_nav).prop('disabled', page >= page_count - 1);

	$prev.off('click.mediaExplorerThumbs').on('click.mediaExplorerThumbs', (event) => {
		event.preventDefault();
		event.stopPropagation();

		if (page <= 0) {
			return;
		}

		fillThumbStrip(instance, $gallery, active_index, { page: page - 1 });
	});

	$next.off('click.mediaExplorerThumbs').on('click.mediaExplorerThumbs', (event) => {
		event.preventDefault();
		event.stopPropagation();

		if (page >= page_count - 1) {
			return;
		}

		fillThumbStrip(instance, $gallery, active_index, { page: page + 1 });
	});
}

/**
 * @param {JQuery} $slide
 * @returns {JQuery}
 */
function buildFrameFromSlide($slide) {
	if (!$slide?.length) {
		return $(`<div class="${FRAME_CLASS}"><div class="${STAGE_CLASS}"></div><div class="${CAPTION_CLASS}"></div><div class="${THUMBS_ROW_CLASS}"><div class="${THUMBS_CLASS}"></div></div></div>`);
	}

	const caption = escapeHtml($slide.attr('title') ?? '');

	return $(
		`<div class="${FRAME_CLASS}">` +
			`<div class="${STAGE_CLASS}">${buildStageHtml($slide)}</div>` +
			`<div class="${CAPTION_CLASS}">${caption}</div>` +
			`<div class="${THUMBS_ROW_CLASS}">` +
			`<button type="button" class="${THUMBS_NAV_CLASS} is-prev" title="Media precedenti" aria-label="Media precedenti"><i class="fa-solid fa-chevron-left" aria-hidden="true"></i></button>` +
			`<div class="${THUMBS_CLASS}" role="tablist"></div>` +
			`<button type="button" class="${THUMBS_NAV_CLASS} is-next" title="Media successivi" aria-label="Media successivi"><i class="fa-solid fa-chevron-right" aria-hidden="true"></i></button>` +
			`</div>` +
			`</div>`,
	);
}

/**
 * @param {JQuery} $el
 * @returns {{source: string|null, identifier: string|null}}
 */
function resolveSourceUsageFromSlide($el) {
	const $host = $el.closest('[data-source][data-source-id], .offcanvas-images[data-agency-id], .offcanvas-images[data-source-id]');

	if (!$host.length) {
		return { source: null, identifier: null };
	}

	return {
		source: $host.attr('data-source') || 'agency',
		identifier: $host.attr('data-source-id') || $host.attr('data-agency-id') || null,
	};
}

/**
 * @param {JQuery} $slide
 * @returns {string}
 */
function buildStageHtml($slide) {
	const media_type = $slide.attr('data-media-type') ?? 'image';
	const caption = escapeHtml($slide.attr('title') ?? '');
	const url = $slide.attr('href') ?? '';
	const usage = resolveSourceUsageFromSlide($slide);
	const usage_attrs =
		usage.source && usage.identifier
			? ` data-source="${escapeAttr(usage.source)}" data-source-id="${escapeAttr(usage.identifier)}" data-media-url="${escapeAttr(url)}"`
			: ` data-media-url="${escapeAttr(url)}"`;

	if (media_type === 'video') {
		const video_type = $slide.attr('data-video-type') ?? 'video';
		const poster = $slide.attr('data-thumb') ?? '';

		if (video_type === 'video') {
			const poster_attr = poster ? ` poster="${escapeAttr(poster)}"` : '';

			return `<div class="mfp-video-container"><video controls preload="metadata"${poster_attr}><source src="${escapeAttr(url)}" type="video/mp4"></video></div>`;
		}

		if (video_type === 'meride') {
			const customer = $slide.attr('data-meride-customer') || window.merideCustomerId;
			const code = $slide.attr('data-meride-code');
			const { html, containerId } = MerideVideo.createPopupContainer(customer, code);
			MerideVideo.initPopupPlayer(containerId, customer, code);

			return html;
		}

		return `<iframe class="mfp-media-explorer-embed" src="${escapeAttr(url)}" frameborder="0" allowfullscreen allow="encrypted-media"></iframe>`;
	}

	if (media_type === 'iframe') {
		return `<iframe class="mfp-media-explorer-embed" src="${escapeAttr(url)}" frameborder="0"></iframe>`;
	}

	if (media_type === 'audio') {
		return `<div class="mfp-audio-container"><audio controls preload="metadata"><source src="${escapeAttr(url)}"></audio></div>`;
	}

	return `<img class="mfp-media-explorer-image" draggable="true" src="${escapeAttr(url)}" alt="${caption}"${usage_attrs}>`;
}

function cleanupMediaExplorerMedia() {
	document.querySelectorAll('.mfp-video-container video, .mfp-audio-container audio').forEach((media) => {
		try {
			media.pause();
			media.currentTime = 0;
			media.removeAttribute('autoplay');
			media.autoplay = false;
			media.removeAttribute('src');
			media.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
			media.load();
		} catch {
			// Detached media elements can throw on load().
		}
	});

	MerideVideo.cleanupPopupPlayers();
}

/**
 * @param {string} media_type
 * @param {string|undefined} video_type
 * @returns {string}
 */
function mediaIconClass(media_type, video_type) {
	if (media_type === 'video') {
		return 'fa-solid fa-video';
	}

	if (media_type === 'audio') {
		return 'fa-solid fa-volume-high';
	}

	if (media_type === 'iframe') {
		return 'fa-solid fa-file-pdf';
	}

	if (video_type && video_type !== 'video') {
		return 'fa-solid fa-play';
	}

	return 'fa-solid fa-image';
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeAttr(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/**
 * @param {Document|Element} [_scope]
 */
export function initMediaExplorer(_scope = document) {
	bindMediaExplorerDocument();
}

export default initMediaExplorer;
